/**
 * `<consumption-panel>` — `SC-F818`, the budget and rate-limit consumption view.
 *
 * It sits on the analytics screen because it shares that screen's window and its
 * ingest watermark, and because the question it answers — "is anybody near their
 * limit?" — is the one the decision breakdown raises when `ERR_BUDGET_EXCEEDED`
 * appears in it.
 *
 * # The panel's whole job is to be honest about three different provenances
 *
 * The number an operator actually wants is the live budget counter, and it is not
 * readable from here: it lives in `budget.db` on the instance's node-local state
 * volume and only `sluice budget show` can open it. So this panel shows
 *
 *   - the AUTHORED limits, exact, from the control plane's own records, with every
 *     way a declared limit is silently enforced by nothing;
 *   - the OBSERVED usage, derived from ingested audit records and therefore fresh
 *     only to the ingest watermark;
 *   - and a named list of what cannot be observed at all,
 *
 * and it renders the API's own `provenance` statements verbatim rather than
 * paraphrasing them, so the API and the screen cannot drift into two different
 * accounts of the same number.
 *
 * # The meter
 *
 * One inline meter per subject: rows served against the rows budget that governs it.
 * It uses the same one-hue magnitude slot every other inline bar in the product uses
 * and switches to a STATUS tone only at the two thresholds — and it is always beside
 * a printed percentage and a badge word, because a status colour that carries meaning
 * alone is unreadable to a third of readers and invisible in forced-colours mode.
 *
 * The meter is absent unless the window is exactly one UTC day. That is the API's
 * decision and this panel does not work around it: the counter it approximates resets
 * at midnight UTC, so a week of rows against a daily cap would render as a 700%
 * overrun — arithmetically correct and meaningless.
 *
 * All four states: loading, empty (no observed subjects, or no authored limits),
 * error (the `apierr` code AND the request id), populated.
 *
 * Stories: `SC-S901`, `SC-S806`.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import type { BnBadgeVariant, BnColumn, BnRow } from "../components/index.js";

import {
  getConsumption,
  type AuthoredLimit,
  type ConsumptionReport,
  type LimitFinding,
  type Provenance,
  type SubjectConsumption,
} from "../api/analytics.js";
import { ApiError, parseError } from "../api/errors.js";

/** The observed table. `rows_served` carries the meter, so it is wider than the
 * counts beside it. */
const SUBJECT_COLUMNS: BnColumn[] = [
  { key: "subject", label: "Subject", mono: true },
  { key: "binding", label: "Governed by" },
  { key: "queries", label: "Queries", width: "1px", align: "right" },
  { key: "rows", label: "Rows served", width: "22%" },
  { key: "budget", label: "Budget refusals", width: "1px", align: "right" },
  { key: "rate", label: "Rate refusals", width: "1px", align: "right" },
];

/** The authored table. */
const LIMIT_COLUMNS: BnColumn[] = [
  { key: "binding", label: "Binding", mono: true },
  { key: "keys", label: "Registered under", mono: true },
  { key: "rate", label: "Rate limit", width: "1px" },
  { key: "budget", label: "Daily budget", width: "1px" },
  { key: "state", label: "Enforced", width: "1px" },
];

/** The two meter thresholds. They are the points at which a reader should act, not
 * a gradient: below the first the bar is the ordinary magnitude hue, and each one
 * above it swaps to a STATUS tone that always ships with the printed percentage. */
const WARN_AT = 75;
const CRITICAL_AT = 100;

/** How the three provenance classes are labelled. `unobservable` is deliberately
 * the most prominent of the three: it is the reason this panel exists in the shape
 * it does. */
const PROVENANCE_LABEL: Record<Provenance["source"], { label: string; variant: BnBadgeVariant }> = {
  authored: { label: "Authored configuration", variant: "info" },
  audit: { label: "From audit records", variant: "neutral" },
  unobservable: { label: "Not observable", variant: "warning" },
};

@customElement("consumption-panel")
export class ConsumptionPanel extends LitElement {
  @property() instanceId = "";
  /** The window, driven by the analytics page so every panel on it covers the same
   * range. RFC 3339 or `YYYY-MM-DD`; empty means the API's own default. */
  @property() from = "";
  @property() to = "";

  @state() private report: ConsumptionReport | null = null;
  @state() private loading = true;
  @state() private error: ApiError | null = null;

  private seq = 0;

  static override styles = css`
    :host {
      /* The series slots, aliased locally rather than inherited. A custom property
         a component only ever inherits renders black the first time it is mounted
         somewhere that does not define it, and every one of these is an alias of a
         --bino-* semantic token so it follows the theme with no media query. */
      --series-neutral: var(--bino-primary);
      --series-warn: var(--bino-warning);
      --series-critical: var(--bino-decision-deny);
      --series-track: var(--bino-surface-inset);
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .lede {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 78ch;
    }
    h3 {
      margin: var(--bino-space-lg) 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    h3:first-of-type {
      margin-top: 0;
    }
    .meta {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 78ch;
    }
    .sub {
      display: block;
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-muted);
    }
    .num {
      font-variant-numeric: tabular-nums;
    }
    /* The meter: a thin track with a 4px-radius fill anchored to the left. The
       track is recessive and the fill carries no text — the percentage is printed
       beside it, which is what lets the status tones mean something to a reader who
       cannot distinguish them. */
    .meter {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
    }
    .track {
      position: relative;
      flex: 1 1 3rem;
      height: 0.5rem;
      border-radius: var(--bino-radius-pill);
      background: var(--series-track);
      overflow: hidden;
    }
    .fill {
      height: 100%;
      border-radius: var(--bino-radius-pill);
      background: var(--series-neutral);
    }
    .fill.warn {
      background: var(--series-warn);
    }
    .fill.critical {
      background: var(--series-critical);
    }
    .pct {
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
      color: var(--bino-text);
    }
    details {
      margin-top: var(--bino-space-md);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      padding: var(--bino-space-sm) var(--bino-space-md);
      background: var(--bino-surface-subtle);
    }
    summary {
      cursor: pointer;
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
    }
    summary:focus-visible {
      outline: var(--bino-focus-ring);
      outline-offset: 2px;
    }
    .prov {
      margin: var(--bino-space-md) 0 0;
      padding: 0;
      list-style: none;
      display: grid;
      gap: var(--bino-space-md);
    }
    .prov-field {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
      display: block;
      margin-top: var(--bino-space-xs);
    }
    .prov p {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 78ch;
    }
    .findings {
      margin: var(--bino-space-sm) 0 0;
      padding: 0;
      list-style: none;
      display: grid;
      gap: var(--bino-space-sm);
    }
    .findings li {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 78ch;
    }
    .findings strong {
      color: var(--bino-text);
    }
    .error-message {
      margin: 0 0 var(--bino-space-sm);
      color: var(--bino-error);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  /** The window is driven by the parent, so a preset change on the analytics page
   * must refetch here. `connectedCallback` does the first load, so this fires only on
   * a genuine change — and the sequence guard in [load] is what keeps an out-of-order
   * response from a rapid preset click out of the render. */
  override willUpdate(changed: Map<string, unknown>) {
    if (this.report === null && this.error === null) return;
    if (changed.has("from") || changed.has("to") || changed.has("instanceId")) {
      void this.load();
    }
  }

  private async load() {
    const mine = ++this.seq;
    this.loading = true;
    try {
      const res = await getConsumption(this.instanceId, {
        from: this.from || undefined,
        to: this.to || undefined,
      });
      if (mine !== this.seq) return;
      this.report = res;
      this.error = null;
    } catch (e) {
      if (mine !== this.seq) return;
      this.error = parseError(e);
      this.report = null;
    } finally {
      if (mine === this.seq) this.loading = false;
    }
  }

  /* ── the meter ─────────────────────────────────────────────────────────── */

  /** meter renders one subject's rows against its budget.
   *
   * When the API withheld the percentage — because the window is not one UTC day —
   * the row shows the raw count and says why the comparison is absent, rather than
   * drawing a bar against a cap the number cannot be compared with. */
  private meter(row: SubjectConsumption): TemplateResult {
    const rows = row.rows_served.toLocaleString();
    if (row.rows_used_percent === undefined) {
      return html`<span class="num">${rows}</span>
        ${row.rows_per_day
          ? html`<span class="sub"
              >of ${row.rows_per_day.toLocaleString()}/day — no share shown: the budget resets at
              midnight UTC, so only a one-day window compares</span
            >`
          : html`<span class="sub">no rows budget governs this subject</span>`}`;
    }
    const pct = row.rows_used_percent;
    const tone = pct >= CRITICAL_AT ? "critical" : pct >= WARN_AT ? "warn" : "";
    const word = pct >= CRITICAL_AT ? "Over budget" : pct >= WARN_AT ? "Near budget" : "";
    return html`<div class="meter">
        <div class="track">
          <div class="fill ${tone}" style="width: ${Math.min(100, pct).toFixed(1)}%"></div>
        </div>
        <span class="pct">${pct.toFixed(0)}%</span>
      </div>
      <span class="sub"
        >${rows} of ${(row.rows_per_day ?? 0).toLocaleString()} rows/day${word
          ? html` · <strong>${word}</strong>`
          : nothing}</span
      >`;
  }

  /* ── tables ────────────────────────────────────────────────────────────── */

  private subjectRows(report: ConsumptionReport): BnRow[] {
    return report.subjects.map((s) => ({
      id: s.subject,
      subject: s.subject,
      binding: s.binding
        ? html`${s.binding}<span class="sub"
              >matched on its ${s.matched_by === "issuer" ? "issuer" : "subject id"}</span
            >`
        : html`<span class="sub"
            >no binding limit${report.limits.available
              ? html` · the server default of ${report.limits.default_subject_rps}/s applies`
              : nothing}</span
          >`,
      queries: html`<span class="num">${s.queries.toLocaleString()}</span>
        ${s.denied > 0
          ? html`<span class="sub">${s.denied.toLocaleString()} denied</span>`
          : nothing}`,
      rows: this.meter(s),
      budget: s.budget_refusals > 0
        ? html`<bn-badge variant="error">${s.budget_refusals.toLocaleString()}</bn-badge>`
        : html`<span class="num">0</span>`,
      rate: s.rate_refusals > 0
        ? html`<bn-badge variant="warning">${s.rate_refusals.toLocaleString()}</bn-badge>`
        : html`<span class="num">0</span>`,
    }));
  }

  private limitRows(report: ConsumptionReport): BnRow[] {
    return report.limits.bindings.map((b) => ({
      id: b.binding,
      binding: html`${b.binding}<span class="sub">${b.mode}</span>`,
      keys:
        b.keys.length === 0
          ? html`<span class="sub">nothing — no subject id and no issuer</span>`
          : html`${b.keys.map(
              (k) => html`${k.value}<span class="sub">${k.kind} key</span>`,
            )}`,
      rate: b.has_rate_limit
        ? html`<span class="num">${b.rps ?? 0}/s</span>
            <span class="sub">burst ${b.burst && b.burst > 0 ? b.burst : 1}</span>`
        : html`<span class="sub"
            >none · server default ${report.limits.default_subject_rps}/s</span
          >`,
      budget: b.has_budget
        ? html`${b.rows_per_day
              ? html`<span class="num">${b.rows_per_day.toLocaleString()} rows/day</span>`
              : nothing}
            ${b.cpu_seconds_per_day
              ? html`<span class="sub">${b.cpu_seconds_per_day} cpu-s/day</span>`
              : nothing}`
        : html`<span class="sub">none</span>`,
      state: this.enforcementBadges(b),
    }));
  }

  /** enforcementBadges is the difference between a number on a form and a number in
   * the engine — the thing this whole panel exists to make visible. */
  private enforcementBadges(b: AuthoredLimit) {
    const badges: TemplateResult[] = [];
    if (b.has_rate_limit) {
      badges.push(
        b.rate_enforced
          ? html`<bn-badge variant="success">Rate</bn-badge>`
          : html`<bn-badge variant="warning">Rate inert</bn-badge>`,
      );
    }
    if (b.has_budget) {
      badges.push(
        b.budget_enforced
          ? html`<bn-badge variant="success">Budget</bn-badge>`
          : html`<bn-badge variant="warning">Budget inert</bn-badge>`,
      );
    }
    return badges.length === 0 ? html`<span class="sub">—</span>` : html`${badges}`;
  }

  /* ── chrome ────────────────────────────────────────────────────────────── */

  /** The findings: every way an authored limit is enforced by nothing, with the
   * upstream citation so a reader can check the claim rather than trust it. */
  private renderFindings(report: ConsumptionReport) {
    const all: LimitFinding[] = [
      ...(report.limits.findings ?? []),
      ...report.limits.bindings.flatMap((b) => b.findings ?? []),
    ];
    if (all.length === 0) return nothing;
    return html`<bn-panel eyebrow="Limits that are enforced by nothing">
      <ul class="findings">
        ${all.map(
          (f) => html`<li>
            <strong>${f.name ? `${f.name}: ` : ""}${f.title ?? f.rule}</strong> ${f.message}
            ${f.upstream ? html`<span class="sub">${f.upstream}</span>` : nothing}
          </li>`,
        )}
      </ul>
    </bn-panel>`;
  }

  /** The provenance block. It is a `<details>` because it is long and because it must
   * be present on every render — the panel's numbers are not defensible without it,
   * and a disclosure a reader can open is not the same as a caveat that is absent. */
  private renderProvenance(report: ConsumptionReport) {
    return html`<details>
      <summary>Where each of these numbers comes from, and what is not here</summary>
      <ul class="prov">
        ${report.provenance.map((p) => {
          const meta = PROVENANCE_LABEL[p.source];
          return html`<li>
            <bn-badge .variant=${meta.variant}>${meta.label}</bn-badge>
            <span class="prov-field">${p.field}</span>
            <p>${p.statement}</p>
            ${p.upstream ? html`<p class="sub">${p.upstream}</p>` : nothing}
          </li>`;
        })}
      </ul>
    </details>`;
  }

  private renderDegraded(report: ConsumptionReport) {
    if (!report.degraded || report.degraded.length === 0) return nothing;
    return html`<bn-panel eyebrow="Columns this deployment could not fill">
      <ul class="findings">
        ${report.degraded.map((d) => html`<li>${d}</li>`)}
      </ul>
    </bn-panel>`;
  }

  private renderError(err: ApiError) {
    return html`<div role="alert">
      <p class="error-message">${err.message}</p>
      <bn-kv
        .rows=${[
          { label: "Error code", value: err.code },
          ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
        ]}
      ></bn-kv>
      ${err.requestId
        ? nothing
        : html`<p class="meta">
            The request did not reach Sluice Cloud, so there is no request id to quote.
          </p>`}
      <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
    </div>`;
  }

  override render() {
    const report = this.report;
    return html`<bn-card heading="Budgets and rate limits">
      <p class="lede">
        Per-subject limits are Sluice's own mechanism and are not the same thing as your
        organization's Sluice Cloud quota. What is authored below is exact; what was consumed is
        derived from audit records; and the live counters your gateway keeps are not readable from
        here at all. Open the disclosure for which is which.
      </p>

      ${this.error
        ? this.renderError(this.error)
        : html`
            ${report ? this.renderDegraded(report) : nothing}

            <h3>Observed in this window</h3>
            <bn-table
              caption="Consumption by subject"
              .columns=${SUBJECT_COLUMNS}
              .rows=${report ? this.subjectRows(report) : []}
              ?loading=${this.loading}
            >
              <div slot="empty">
                <bn-empty-state
                  icon="∅"
                  heading="No subject activity in this window"
                  message="No ingested audit record in this range names a subject. That is not the same as nobody having queried: records arrive up to the ingest watermark, so a very recent window can legitimately be empty."
                ></bn-empty-state>
              </div>
            </bn-table>
            ${report && report.truncated
              ? html`<p class="meta">
                  This is a ranked top-N rather than every subject, and the issuer attribution pass
                  may have been capped — some subjects can therefore read as governed by no binding
                  when an issuer-level limit does govern them.
                </p>`
              : nothing}
            ${report && report.unclaimed > 0
              ? html`<p class="meta">
                  ${report.unclaimed} of the subjects above are claimed by no binding's limit. They
                  are bounded by the server configuration's default subject rate and by no budget.
                </p>`
              : nothing}

            <h3>Authored limits</h3>
            ${report && !report.limits.available
              ? html`<bn-panel eyebrow="The authored half is unavailable">
                  <p class="meta">
                    This deployment could not read the instance's <code>SubjectBinding</code>
                    objects, so there is nothing to compare the numbers above against. The limits
                    themselves are on the subject-binding surface.
                  </p>
                </bn-panel>`
              : html`
                  <bn-table
                    caption="Authored per-subject limits"
                    .columns=${LIMIT_COLUMNS}
                    .rows=${report ? this.limitRows(report) : []}
                    ?loading=${this.loading}
                  >
                    <div slot="empty">
                      <bn-empty-state
                        icon="∅"
                        heading="No per-subject limit is authored"
                        message="No SubjectBinding on this instance declares a rate limit or a daily budget, so every subject is bounded only by the server configuration's defaults."
                      ></bn-empty-state>
                    </div>
                  </bn-table>
                  ${report
                    ? html`<p class="meta">
                        Generation ${report.limits.generation ?? "—"}
                        ${report.limits.generation_state
                          ? html`(${report.limits.generation_state})`
                          : nothing}
                        · budget subsystem
                        ${report.limits.budget_enabled === null
                          ? html`<bn-badge variant="neutral">unknown</bn-badge>`
                          : report.limits.budget_enabled
                            ? html`<bn-badge variant="success">enabled</bn-badge>`
                            : html`<bn-badge variant="warning">off</bn-badge>`}
                      </p>`
                    : nothing}
                `}
            ${report ? this.renderFindings(report) : nothing}
            ${report ? this.renderProvenance(report) : nothing}
          `}
    </bn-card>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "consumption-panel": ConsumptionPanel;
  }
}
